import { useState, useEffect, useRef, useCallback } from "react";
import { Camera, Pin } from "lucide-react";

/**
 * R22-MVP — Snapshot Mosaic Camera Grid.
 *
 * Shows near-live JPEG thumbnails (refreshed every 5-10s) for all participants
 * in the room. Clicking a tile pins it (upgrades to real-time video via the
 * existing CamTile pipeline). This replaces the CrowdWall for cam-on members
 * who aren't pinned, saving massive bandwidth vs. streaming low-res video.
 *
 * Props:
 *   - roomId: current room ID
 *   - identities: array of { identity, display_name, role, is_dj } for crowd members
 *   - pinnedIdentities: currently pinned set
 *   - onPin: (identity) => void
 *   - pinDisabled: boolean (max 9 reached)
 *   - backendUrl: REACT_APP_BACKEND_URL
 */
export default function SnapshotGrid({ roomId, identities, pinnedIdentities, onPin, pinDisabled, backendUrl }) {
  const [snapshots, setSnapshots] = useState({}); // { identity: blobUrl }
  const intervalRef = useRef(null);
  const pinnedSet = new Set(pinnedIdentities || []);

  // Fetch snapshots periodically
  const fetchSnapshots = useCallback(async () => {
    if (!roomId || !identities?.length) return;
    try {
      const resp = await fetch(`${backendUrl}/api/rooms/${encodeURIComponent(roomId)}/snapshots`, {
        credentials: "include",
      });
      if (!resp.ok) return;
      const data = await resp.json();
      const available = new Set(data.snapshots.map((s) => s.identity));

      // Fetch individual snapshots for available identities (limit to 30 at a time)
      const toFetch = identities
        .filter((m) => available.has(m.identity) && !pinnedSet.has(m.identity))
        .slice(0, 30);

      const newSnaps = {};
      await Promise.allSettled(
        toFetch.map(async (m) => {
          try {
            const r = await fetch(
              `${backendUrl}/api/rooms/${encodeURIComponent(roomId)}/snapshot/${encodeURIComponent(m.identity)}`,
              { credentials: "include" }
            );
            if (r.ok) {
              const blob = await r.blob();
              newSnaps[m.identity] = URL.createObjectURL(blob);
            }
          } catch { /* ignore individual failures */ }
        })
      );

      // Revoke old blob URLs to prevent memory leaks
      setSnapshots((prev) => {
        Object.values(prev).forEach((url) => {
          try { URL.revokeObjectURL(url); } catch { /* ignore */ }
        });
        return newSnaps;
      });
    } catch { /* ignore fetch errors */ }
  }, [roomId, identities, backendUrl, pinnedSet]);

  useEffect(() => {
    fetchSnapshots();
    intervalRef.current = setInterval(fetchSnapshots, 7000); // Refresh every 7s
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      // Cleanup blob URLs
      setSnapshots((prev) => {
        Object.values(prev).forEach((url) => {
          try { URL.revokeObjectURL(url); } catch { /* ignore */ }
        });
        return {};
      });
    };
  }, [fetchSnapshots]);

  // Filter to only show non-pinned identities that have snapshots or are in the crowd
  const visibleTiles = identities.filter((m) => !pinnedSet.has(m.identity));

  if (visibleTiles.length === 0) return null;

  return (
    <section className="mt-4 pt-3 border-t border-white/[0.06]" data-testid="snapshot-grid">
      <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-2.5 flex items-center gap-2">
        <Camera className="w-3 h-3" />
        Camera Grid
        <span className="opacity-65">
          · {visibleTiles.length} participant{visibleTiles.length === 1 ? "" : "s"}
        </span>
      </div>
      <div className="grid gap-2 grid-cols-[repeat(auto-fill,minmax(100px,1fr))] sm:grid-cols-[repeat(auto-fill,minmax(120px,1fr))]">
        {visibleTiles.slice(0, 60).map((m) => {
          const snapUrl = snapshots[m.identity];
          return (
            <div
              key={m.identity}
              className="relative aspect-[4/3] rounded-lg overflow-hidden bg-white/[0.04] cursor-pointer group hover:ring-1 hover:ring-[#00F0FF]/50 transition-all"
              onClick={() => !pinDisabled && onPin?.(m.identity)}
              title={pinDisabled ? "Max 9 pins reached" : `Pin ${m.display_name || "participant"} for HD video`}
              data-testid={`snapshot-tile-${m.identity}`}
            >
              {snapUrl ? (
                <img
                  src={snapUrl}
                  alt={m.display_name || ""}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-[11px] font-bold text-white/60">
                    {(m.display_name || "?")[0]?.toUpperCase()}
                  </div>
                </div>
              )}
              {/* Name overlay */}
              <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent px-1.5 py-1">
                <div className="text-[9px] text-white/80 truncate font-medium">
                  {m.display_name || m.identity?.slice(0, 8)}
                </div>
              </div>
              {/* Pin hint on hover */}
              {!pinDisabled && (
                <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Pin className="w-3 h-3 text-amber-300" />
                </div>
              )}
              {/* Role badges */}
              {m.is_dj && (
                <div className="absolute top-1 left-1 px-1 py-0.5 rounded bg-[#FF007F]/80 text-[8px] font-bold text-white">
                  DJ
                </div>
              )}
            </div>
          );
        })}
        {visibleTiles.length > 60 && (
          <div className="aspect-[4/3] rounded-lg bg-white/[0.04] grid place-items-center text-[11px] text-white/45">
            +{visibleTiles.length - 60} more
          </div>
        )}
      </div>
    </section>
  );
}
